export interface CampDataVersionContext {
  version: string
  dataRoot: string
}

export type CampDataRuntimeConfig =
  | { source: 'local'; version: string; dataRoot: string }
  | { source: 'remote'; toolId: string }

export type GameToolVersionFetcher = (toolId: string, version?: string) => Promise<unknown>

type RuntimeEnv = Record<string, string | undefined>

interface GameToolVersionResponse {
  bcode?: unknown
  sdkcode?: unknown
  versionInfo?: { version?: unknown; dataBaseUrl?: unknown }
  response?: {
    res?: unknown
    versionInfo?: { version?: unknown; dataBaseUrl?: unknown }
    data?: { versionInfo?: { version?: unknown; dataBaseUrl?: unknown } }
  }
}

let activeContext: CampDataVersionContext | null = null

export function resolveCampDataRuntimeConfig(env: RuntimeEnv): CampDataRuntimeConfig {
  if (env.VITE_DATA_SOURCE === 'remote') {
    return { source: 'remote', toolId: requireValue(env.VITE_TOOL_ID, 'VITE_TOOL_ID is required when VITE_DATA_SOURCE=remote') }
  }

  if (env.VITE_DATA_SOURCE === 'local') {
    return {
      source: 'local',
      version: requireValue(env.VITE_LOCAL_VERSION, 'VITE_LOCAL_VERSION is required when VITE_DATA_SOURCE=local'),
      dataRoot: buildVersionRoot(requireValue(env.VITE_LOCAL_DATA_ROOT, 'VITE_LOCAL_DATA_ROOT is required when VITE_DATA_SOURCE=local'), requireValue(env.VITE_LOCAL_VERSION, 'VITE_LOCAL_VERSION is required when VITE_DATA_SOURCE=local')),
    }
  }

  throw new Error('VITE_DATA_SOURCE must be "local" or "remote"')
}

export function getCampDataRuntimeConfig(): CampDataRuntimeConfig {
  return resolveCampDataRuntimeConfig(import.meta.env)
}

export async function initializeCampDataVersion(
  config: CampDataRuntimeConfig,
  location: Pick<Location, 'search' | 'hash'>,
  getVersion: GameToolVersionFetcher,
): Promise<CampDataVersionContext> {
  if (config.source === 'local') return { version: config.version, dataRoot: config.dataRoot }

  return parseGameToolVersionResponse(await getVersion(config.toolId, resolveRequestedDataVersion(location.search, location.hash)))
}

export function setCampDataVersionContext(context: CampDataVersionContext): void {
  activeContext = context
}

export function getCampDataVersionContext(): CampDataVersionContext {
  if (!activeContext) throw new Error('Camp data version must be initialized before loading data')
  return activeContext
}

export function resolveRequestedDataVersion(search: string, hash: string): string | undefined {
  return readDataVersion(search) ?? readDataVersion(hash.slice(hash.indexOf('?')))
}

export function parseGameToolVersionResponse(input: unknown): CampDataVersionContext {
  const result = input as GameToolVersionResponse
  const versionInfo = result.versionInfo ?? result.response?.versionInfo ?? result.response?.data?.versionInfo
  if (result.bcode !== 0 || result.sdkcode !== 0 || result.response?.res !== 200) {
    throw new Error('Game tool version service returned an unsuccessful response')
  }
  if (!isNonEmptyString(versionInfo?.version) || !isNonEmptyString(versionInfo?.dataBaseUrl)) {
    throw new Error('Game tool version service response is missing version information')
  }
  return { version: versionInfo.version, dataRoot: buildVersionRoot(versionInfo.dataBaseUrl, versionInfo.version) }
}

function buildVersionRoot(dataBaseUrl: string, version: string): string {
  return `${dataBaseUrl.replace(/\/+$/, '')}/${version}`
}

function readDataVersion(query: string): string | undefined {
  const version = new URLSearchParams(query).get('dataVer')?.trim()
  return version || undefined
}

function requireValue(value: string | undefined, message: string): string {
  if (!value?.trim()) throw new Error(message)
  return value
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}
